import CajasService from '@/api/services/cajas.service';
import type { ICaja } from '@/interfaces/ICaja';
import type { ICajaMovement } from '@/interfaces/ICajaMovement';
import { HostToolError, type IGpthelpTool } from '../tool-types';
import {
  getLimitArg,
  getOptionalEnum,
  getOptionalNumber,
  normalizeInstantRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensureCajaReadAccess } from '../tool-scope';
import { capList, omitNullish, sanitizeCajaResume } from '../sanitize';

export const cajaTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'caja_list',
      description:
        'List cajas available for the selected restaurant + branch (already filtered by your caja permissions).',
      sideEffects: 'none',
      parameters: { type: 'object', additionalProperties: false, properties: {} },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope &&
      gate.branchUsesCajas &&
      (gate.canViewStats || gate.canManageCaja),
    async execute(_args, { deps, scope }) {
      ensureCajaReadAccess({ deps, scope });

      const cajas = await runToolApiCall(() =>
        CajasService.getAll(scope.restaurantId, scope.branchId),
      );
      const results = (cajas ?? []).map((c: ICaja) => ({
        cajaId: c.id,
        name: c.name,
        enabled: c.enabled ?? null,
      }));

      return { count: results.length, results };
    },
  },
  {
    definition: {
      name: 'caja_resume',
      description:
        'Get a caja resume snapshot (aggregated totals). Output is sanitized (no customer PII).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          cajaId: { type: 'number' },
        },
        required: ['cajaId'],
      },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope &&
      gate.branchUsesCajas &&
      (gate.canViewStats || gate.canManageCaja),
    async execute(args, { deps, scope }) {
      ensureCajaReadAccess({ deps, scope });

      const cajaIdRaw = getOptionalNumber(args, 'cajaId');
      if (cajaIdRaw === null) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "cajaId"', {
          cajaId: args.cajaId,
        });
      }
      const cajaId = Math.floor(cajaIdRaw);
      if (!Number.isFinite(cajaId) || cajaId <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "cajaId"', { cajaId });
      }

      const resume = await runToolApiCall(() =>
        CajasService.getResume(scope.restaurantId, scope.branchId, cajaId),
      );
      return sanitizeCajaResume(resume);
    },
  },
  {
    definition: {
      name: 'caja_closes',
      description:
        'Historic caja CLOSE snapshots: frozen resumes taken at close time (sales by payment method, cash counts, withdrawals/deposits, canceled orders). A caja session can span business days; for per-day sales use stats_orders, for per-day money collected, over/short or tenders dayclose_summary. caja_resume is the LIVE open session only.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: {
            type: 'string',
            description:
              'Start date (yyyy-MM-dd, branch-local) or ISO instant. Required.',
          },
          to: { type: 'string', description: RANGE_END_DESCRIPTION },
          cajaId: { type: 'number', description: 'Filter to one caja.' },
          detail: {
            type: 'string',
            enum: ['summary', 'full'],
            default: 'summary',
            description:
              'summary: compact per-close rows (max 50). full: entire sanitized resumes (max 5).',
          },
          limit: {
            type: 'number',
            minimum: 1,
            maximum: 50,
            description: 'Max closes returned (cap 50 summary / 5 full).',
          },
        },
        required: ['from'],
      },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope &&
      gate.branchUsesCajas &&
      (gate.canViewStats || gate.canManageCaja),
    async execute(args, { deps, scope }) {
      ensureCajaReadAccess({ deps, scope });

      const detail =
        getOptionalEnum(args, 'detail', ['summary', 'full'] as const) ?? 'summary';
      const maxLimit = detail === 'full' ? 5 : 50;
      const limit = getLimitArg(args, {
        def: detail === 'full' ? 3 : 20,
        max: maxLimit,
      });

      const cajaIdRaw = getOptionalNumber(args, 'cajaId');
      const cajaId = cajaIdRaw !== null ? Math.floor(cajaIdRaw) : undefined;

      const { from, to } = normalizeInstantRange({
        obj: args,
        fromKey: 'from',
        toKey: 'to',
        timeZone: scope.timeZone,
      });
      if (!from) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "from"', {
          from: args.from,
        });
      }

      const movements = await runToolApiCall(() =>
        CajasService.getHistoric({
          restaurantId: scope.restaurantId,
          branchId: scope.branchId,
          cajaId,
          dateFrom: from,
          dateTo: to ?? undefined,
        }),
      );

      const closes = (movements ?? []).filter(
        (m: ICajaMovement) => m.type === 'close',
      );

      const mapClose = (m: ICajaMovement) => {
        const resume = m.data ? sanitizeCajaResume(m.data) : null;
        if (detail === 'full') {
          return omitNullish({
            movementId: m.id,
            cajaId: m.cajaId,
            createdAt: m.createdAt,
            resume,
          });
        }
        return omitNullish({
          movementId: m.id,
          cajaId: m.cajaId,
          cajaName: resume?.caja?.name ?? m.caja?.name ?? null,
          openDate: resume?.movementsResume?.openDate ?? null,
          closeDate: resume?.movementsResume?.closeDate ?? m.createdAt ?? null,
          cashOnOpen: resume?.movementsResume?.cashOnOpen ?? null,
          cashOnClose: resume?.movementsResume?.cashOnClose ?? null,
          withdrawals: resume?.movementsResume?.withdrawals ?? null,
          deposits: resume?.movementsResume?.deposits ?? null,
          salesByPaymentMethod: resume?.salesByPaymentMethod ?? null,
          sessionsTotals: resume?.sessionsTotals ?? null,
          waterfall: resume?.waterfall ?? null,
          nonSaleCollections: resume?.nonSaleCollections ?? null,
        });
      };

      return { totalCloses: closes.length, ...capList(closes.map(mapClose), limit) };
    },
  },
];
